import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Icon,
  Badge,
  Progress,
  SimpleGrid,
  useColorModeValue,
} from '@chakra-ui/react';
import { Activity, Heart, Brain, Clock, Zap, type LucideIcon } from 'lucide-react';

type DomainType = 'AQ' | 'CQ' | 'WQ' | 'ALL';

interface Activity {
  id: string;
  title: string;
  description: string;
  duration: string;
  icon: LucideIcon;
  progress: number;
  domain: DomainType;
  category?: string;
}

interface MobileResilienceActivitiesProps {
  onSelectActivity: (activityId: string) => void;
  activities: Activity[];
}

const MobileResilienceActivities: React.FC<MobileResilienceActivitiesProps> = ({ onSelectActivity, activities }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const secondaryTextColor = useColorModeValue('gray.600', 'gray.400');

  // Methodist color palette
  const methodistBlue = '#0066CC';
  const methodistGreen = '#38A169';

  // Filter core activities and other activities
  const coreActivities = activities.filter(a => 
    a.title.includes('Mindful Breathing') || 
    a.title.includes('Quick Stretch') || 
    a.title.includes('Gratitude Journal')
  );
  
  const otherActivities = activities.filter(a => 
    !a.title.includes('Mindful Breathing') && 
    !a.title.includes('Quick Stretch') && 
    !a.title.includes('Gratitude Journal')
  );

  return (
    <Box bg="gray.50" minH="100vh">
      <Box p={4} pt={8}>
        <HStack spacing={2} mb={6}>
          <Icon as={Zap} color="blue.500" />
          <Text fontSize="xl" fontWeight="semibold" color="blue.500">
            SentientIQ Adaptability
          </Text>
        </HStack>

        {/* Activities Header */}
        <VStack align="flex-start" spacing={1} mb={6}>
          <Text fontSize="2xl" fontWeight="bold">
            Activities
          </Text>
          <Text color="gray.600" fontSize="sm">
            Personalized for you
          </Text>
        </VStack>

        {/* Core Activities */}
        <Text fontSize="lg" fontWeight="semibold" mb={4}>
          Daily Practices
        </Text>
        <SimpleGrid columns={1} spacing={4} mb={6}>
          {coreActivities.map((activity, index) => (
            <Box
              key={activity.id}
              onClick={() => onSelectActivity(activity.id)}
              bg="white"
              p={4}
              borderRadius="lg"
              shadow="sm"
              borderWidth={1}
              borderColor="gray.100"
              cursor="pointer"
              _hover={{ bg: 'gray.50' }}
              role="button"
            >
              <HStack spacing={4} align="flex-start">
                <Box
                  p={2}
                  bg={activity.domain === 'AQ' ? 'green.50' : 'blue.50'}
                  borderRadius="md"
                  color={activity.domain === 'AQ' ? 'green.500' : 'blue.500'}
                >
                  <Icon as={activity.icon} boxSize={5} />
                </Box>
                <VStack align="flex-start" spacing={1} flex={1}>
                  <Text fontSize="md" fontWeight="semibold">
                    {activity.title}
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    {activity.description}
                  </Text>
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    {activity.duration}
                  </Text>
                  {activity.progress > 0 && (
                    <Box width="100%" mt={2}>
                      <HStack justify="space-between" fontSize="xs" mb={1}>
                        <Text color="gray.500">Progress</Text>
                        <Text fontWeight="medium" color={activity.domain === 'AQ' ? 'green.500' : 'blue.500'}>
                          {activity.progress}%
                        </Text>
                      </HStack>
                      <Progress 
                        value={activity.progress} 
                        size="xs" 
                        colorScheme={activity.domain === 'AQ' ? 'green' : 'blue'} 
                        borderRadius="full" 
                      />
                    </Box>
                  )}
                </VStack>
              </HStack>
            </Box>
          ))}
        </SimpleGrid>
        
        {/* Other Activities */}
        <Text fontSize="lg" fontWeight="semibold" mb={4}>
          Curated Activities
        </Text>
        <SimpleGrid columns={1} spacing={4}>
          {otherActivities.map((activity, index) => (
            <Box
              key={activity.id}
              onClick={() => onSelectActivity(activity.id)}
              bg="white"
              p={4}
              borderRadius="lg"
              shadow="sm"
              borderWidth={1}
              borderColor="gray.100"
              cursor="pointer"
              _hover={{ bg: 'gray.50' }}
              role="button"
            >
              <HStack spacing={4} align="flex-start">
                <Box
                  p={2}
                  bg={activity.domain === 'AQ' ? 'green.50' : 'blue.50'}
                  borderRadius="md"
                  color={activity.domain === 'AQ' ? 'green.500' : 'blue.500'}
                >
                  <Icon as={activity.icon} boxSize={5} />
                </Box>
                <VStack align="flex-start" spacing={1} flex={1}>
                  <Text fontSize="md" fontWeight="semibold">
                    {activity.title}
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    {activity.description}
                  </Text>
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    {activity.duration}
                  </Text>
                </VStack>
              </HStack>
            </Box>
          ))}
        </SimpleGrid>
      </Box>
    </Box>
  );
};

export default MobileResilienceActivities;
